import React, {createContext, useCallback, useContext, useState} from 'react';
import {App as AntdApp, ConfigProvider, theme as antdTheme} from 'antd';
import {
	getThemePreset,
	setThemePreset as setThemePresetStorage,
	type ThemePreset,
} from '../settings/themePreset';

/** Светлый оттенок синего для фона (синяя тема). */
const BLUE_BG = '#F3F7FC';
const BLUE_PRIMARY = '#1677ff';

/** Палитра в стиле Windows/Fluent — светлая тема. */
const WINDOWS_LIGHT = {
	colorBgContainer: '#ffffff',
	colorBgLayout: '#f3f3f3',
	colorBgElevated: '#ffffff',
	colorBorder: '#e5e5e5',
	colorBorderSecondary: '#f0f0f0',
};

/** Палитра в стиле Windows/Fluent — тёмная тема. */
const WINDOWS_DARK = {
	colorBgContainer: '#202020',
	colorBgLayout: '#1f1f1f',
	colorBgElevated: '#2d2d2d',
	colorBorder: '#3f3f3f',
	colorBorderSecondary: '#383838',
};

type ThemeContextValue = {
	themePreset: ThemePreset;
	setThemePreset: (value: ThemePreset) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useThemePalette() {
	const ctx = useContext(ThemeContext);
	if (!ctx) throw new Error('useThemePalette must be used within ThemeProvider');
	return ctx;
}

type ThemeProviderProps = {
	children: React.ReactNode;
};

export function ThemeProvider({children}: ThemeProviderProps) {
	const [themePreset, setState] = useState<ThemePreset>(() => getThemePreset());

	const setThemePreset = useCallback((value: ThemePreset) => {
		setThemePresetStorage(value);
		setState(value);
	}, []);

	const theme = React.useMemo(() => {
		if (themePreset === 'light') {
			return {
				cssVar: {key: 'canvas-light'},
				token: {
					colorPrimary: BLUE_PRIMARY,
					borderRadius: 6,
					...WINDOWS_LIGHT,
				},
				components: {Card: {colorBgContainer: WINDOWS_LIGHT.colorBgContainer}},
			};
		}
		if (themePreset === 'dark') {
			return {
				cssVar: {key: 'canvas-dark'},
				algorithm: antdTheme.darkAlgorithm,
				token: {
					colorPrimary: BLUE_PRIMARY,
					borderRadius: 6,
					...WINDOWS_DARK,
				},
			};
		}

		return {
			cssVar: {key: 'canvas-blue'},
			token: {
				colorPrimary: BLUE_PRIMARY,
				borderRadius: 6,
				colorBgContainer: BLUE_BG,
				colorBgLayout: BLUE_BG,
			},
			components: {Card: {colorBgContainer: '#ffffff'}},
		};
	}, [themePreset]);

	return (
		<ThemeContext.Provider value={{themePreset, setThemePreset}}>
			<ConfigProvider key={themePreset} theme={theme}>
				<AntdApp>{children}</AntdApp>
			</ConfigProvider>
		</ThemeContext.Provider>
	);
}
